import "./SetUp.css";
import "./Billing.css";
import ManualBillingCard from '../components/billing/ManualBillingCard';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  List,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  FundProjectionScreenOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { useGlobalToast } from "../context/ToastProvider";
import {
  changeBillingSelection,
  createBillingCheckoutSession,
  createBillingPortalSession,
  getBillingOverview,
  startBillingTrial,
  updateBillingProfile,
} from "../services/billingService";

const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due", "unpaid"]);

function formatCurrency(amount, currency = "EUR") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatCompactQuantity(value) {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0";
  return new Intl.NumberFormat(undefined, {
    notation: Math.abs(numericValue) >= 1000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(numericValue) >= 1000 ? 1 : 0,
  }).format(numericValue);
}

function formatRangeLabel(min, max, t, unitKey) {
  if (max == null) {
    return t("billing.range.from", {
      defaultValue: "{{min}}+ {{unit}}",
      min: formatCompactQuantity(min),
      unit: t(unitKey),
    });
  }
  return t("billing.range.between", {
    defaultValue: "{{min}}-{{max}} {{unit}}",
    min: formatCompactQuantity(min),
    max: formatCompactQuantity(max),
    unit: t(unitKey),
  });
}

function subscriptionStatusColor(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "active" || normalized === "trialing") return "green";
  if (normalized === "past_due" || normalized === "unpaid") return "orange";
  if (normalized === "cancelled" || normalized === "canceled") return "red";
  return "default";
}

function buildDandfPlanFeatures(plan, t) {
  return [
    t("billing.features.driverPricingTier", {
      range: formatRangeLabel(plan.minDrivers, plan.maxDrivers, t, "billing.units.drivers"),
    }),
    plan.sponsorsPremium
      ? t("billing.features.driverPremiumIncluded")
      : t("billing.features.driverPremiumExcluded"),
  ];
}

function buildEtdPlanFeatures(plan, t) {
  const minimumCommitment = Number(plan.minCommitment || 0);
  const minimumCommitmentPrice = minimumCommitment * Number(plan.unitAmountNet || 0);
  return [
    formatRangeLabel(plan.minCommitment, plan.maxCommitment, t, "billing.units.edocs"),
    minimumCommitment > 0
      ? t("billing.features.minimumCommitmentFrom", {
          count: formatCompactQuantity(minimumCommitment),
          total: formatCurrency(minimumCommitmentPrice, plan.currency || "EUR"),
        })
      : t("billing.features.noMinimumCommitment"),
    t("billing.features.onlyIssuedBilled"),
  ];
}

function isPaidPlan(plan) {
  return Number(plan?.unitAmountNet || 0) > 0;
}

export default function Billing() {
  const { getFleetID } = useContext(AuthContext);
  const fleetId = getFleetID();
  const { t } = useTranslation();
  const toast = useGlobalToast();

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSelection, setSavingSelection] = useState(false);
  const [launchingCheckout, setLaunchingCheckout] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);

  const [legalName, setLegalName] = useState("");
  const [contactName, setContactName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [selectedDandfPlanKey, setSelectedDandfPlanKey] = useState("");
  const [selectedEtdPlanKey, setSelectedEtdPlanKey] = useState("");

  const loadOverview = async () => {
    if (!fleetId) return;
    try {
      setLoading(true);
      const response = await getBillingOverview(fleetId);
      setOverview(response);
    } catch (error) {
      console.error("Error loading billing overview:", error);
      toast({ error,
        title: t("billing.errors.loadOverview"),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [fleetId]);

  useEffect(() => {
    if (!overview) return;
    const visibleDandfPlans = (overview.catalog?.dandf || []).filter(isPaidPlan);
    const visibleEtdPlans = overview.catalog?.etd || [];
    const overviewDandfPlanKey = String(overview.selection?.dandf?.planKey || "");
    const overviewEtdPlanKey = String(overview.selection?.etd?.planKey || "");
    setLegalName(String(overview.profile?.legalName || ""));
    setContactName(String(overview.profile?.contactName || ""));
    setBillingEmail(String(overview.profile?.email || ""));
    setBillingPhone(String(overview.profile?.phone || ""));
    setVatNumber(String(overview.profile?.vatNumber || ""));
    setBillingAddress(String(overview.profile?.billingAddress || ""));
    setSelectedDandfPlanKey(
      visibleDandfPlans.some((plan) => plan.key === overviewDandfPlanKey)
        ? overviewDandfPlanKey
        : visibleDandfPlans[0]?.key || overviewDandfPlanKey
    );
    setSelectedEtdPlanKey(
      visibleEtdPlans.some((plan) => plan.key === overviewEtdPlanKey)
        ? overviewEtdPlanKey
        : visibleEtdPlans[0]?.key || ""
    );
  }, [overview]);

  const allDandfPlans = overview?.catalog?.dandf || [];
  const dandfPlans = allDandfPlans.filter(isPaidPlan);
  const etdPlans = overview?.catalog?.etd || [];
  const selectedDandfPlan = dandfPlans.find((plan) => plan.key === selectedDandfPlanKey) || dandfPlans[0] || null;
  const selectedEtdPlan = etdPlans.find((plan) => plan.key === selectedEtdPlanKey) || etdPlans[0] || null;
  const selectedEtdCommitmentQuantity = selectedEtdPlan ? Number(selectedEtdPlan.minCommitment || 0) : 0;
  const currentSubscriptionStatus = String(overview?.currentSubscription?.status || "inactive").toLowerCase();
  const holdedConfigured = Boolean(overview?.providerStatus?.holdedConfigured);
  const hasActiveSubscription = ACTIVE_STATUSES.has(currentSubscriptionStatus);
  const selectionDirty =
    selectedDandfPlanKey !== String(overview?.selection?.dandf?.planKey || "")
    || selectedEtdPlanKey !== String(overview?.selection?.etd?.planKey || "");
  const profileDirty =
    legalName !== String(overview?.profile?.legalName || "")
    || contactName !== String(overview?.profile?.contactName || "")
    || billingEmail !== String(overview?.profile?.email || "")
    || billingPhone !== String(overview?.profile?.phone || "")
    || vatNumber !== String(overview?.profile?.vatNumber || "")
    || billingAddress !== String(overview?.profile?.billingAddress || "");

  const payableSelection = useMemo(
    () => Boolean((selectedDandfPlan && Number(selectedDandfPlan.unitAmountNet || 0) > 0) || selectedEtdPlan),
    [selectedDandfPlan, selectedEtdPlan]
  );

  const currentDandfPlanKey =
    overview?.currentSubscription?.itemsByFamily?.dandf?.planKey || overview?.selection?.dandf?.planKey;
  const currentEtdPlanKey =
    overview?.currentSubscription?.itemsByFamily?.etd?.planKey || overview?.selection?.etd?.planKey;
  const currentDandfPlan = allDandfPlans.find((plan) => plan.key === currentDandfPlanKey) || null;
  const currentEtdPlan = etdPlans.find((plan) => plan.key === currentEtdPlanKey) || null;
  const trial = overview?.trial || {};
  const trialActive = String(trial.status || "").toLowerCase() === "active";
  const trialStartAvailable = trial.startAvailable === true;

  useEffect(() => {
    if (overview?.fleetId && overview?.operationsAccess) {
      window.dispatchEvent(new CustomEvent("fleet-plan-blocked", {
        detail: { fleetId: overview.fleetId, access: overview.operationsAccess },
      }));
    }
  }, [overview]);

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const nextOverview = await updateBillingProfile(fleetId, {
        legalName,
        contactName,
        email: billingEmail,
        phone: billingPhone,
        vatNumber,
        billingAddress,
      });
      setOverview(nextOverview);
      toast({
        title: t("billing.profile.updated"),
        status: "success",
      });
    } catch (error) {
      console.error("Error updating billing profile:", error);
      toast({ error,
        title: t("billing.errors.saveProfile"),
        status: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSelection = async () => {
    try {
      setSavingSelection(true);
      const nextOverview = await changeBillingSelection(fleetId, {
        dandfPlanKey: selectedDandfPlanKey,
        etdPlanKey: selectedEtdPlanKey,
        etdCommitmentQuantity: selectedEtdPlanKey ? Number(selectedEtdCommitmentQuantity || 0) : 0,
      });
      setOverview(nextOverview);
      toast({
        title: nextOverview?.subscriptionUpdated
          ? t("billing.selection.applied")
          : t("billing.selection.saved"),
        status: "success",
      });
    } catch (error) {
      console.error("Error changing billing selection:", error);
      toast({ error,
        title: t("billing.errors.saveSelection"),
        status: "error",
      });
    } finally {
      setSavingSelection(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setStartingTrial(true);
      const nextOverview = await startBillingTrial(fleetId);
      setOverview(nextOverview);
      toast({
        title: t("billing.trial.started"),
        status: "success",
      });
    } catch (error) {
      console.error("Error starting billing trial:", error);
      toast({ error,
        title: t("billing.errors.startTrial"),
        status: "error",
      });
    } finally {
      setStartingTrial(false);
    }
  };

  const handleCheckout = async () => {
    try {
      setLaunchingCheckout(true);
      if (selectionDirty) {
        const savedOverview = await changeBillingSelection(fleetId, {
          dandfPlanKey: selectedDandfPlanKey,
          etdPlanKey: selectedEtdPlanKey,
          etdCommitmentQuantity: selectedEtdPlanKey ? Number(selectedEtdCommitmentQuantity || 0) : 0,
        });
        setOverview(savedOverview);
      }

      const session = await createBillingCheckoutSession(fleetId);
      if (session?.checkoutUrl) {
        window.location.assign(session.checkoutUrl);
        return;
      }

      throw new Error("Checkout URL missing");
    } catch (error) {
      console.error("Error launching billing checkout:", error);
      toast({ error,
        title: t("billing.errors.checkout"),
        status: "error",
      });
    } finally {
      setLaunchingCheckout(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      setOpeningPortal(true);
      const session = await createBillingPortalSession(fleetId);
      if (session?.portalUrl) {
        window.open(session.portalUrl, "_blank", "noopener,noreferrer");
        return;
      }
      throw new Error("Portal URL missing");
    } catch (error) {
      console.error("Error opening billing portal:", error);
      toast({ error,
        title: t("billing.errors.portal"),
        status: "error",
      });
    } finally {
      setOpeningPortal(false);
    }
  };

  if (!fleetId) {
    return (
      <div className="setup-page">
        <div className="setup-page-inner">
          <Card className="card-container" style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Typography.Title level={5} style={{ textAlign: "center" }}>
              {t("billing.loading")}
            </Typography.Title>
          </Card>
        </div>
      </div>
    );
  }

  const documentSectionTitle = holdedConfigured
    ? t("billing.invoices.title")
    : t("billing.invoices.titleReceipts");
  const documentEmptyText = holdedConfigured
    ? t("billing.invoices.empty")
    : t("billing.invoices.emptyReceipts");

  return (
    <div className="setup-page">
      <div className="setup-page-inner">
        <Card className="setup-hero" bordered={false} loading={loading}>
          <Space direction="vertical" size={4}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {t("navbar.billing")}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t("billing.subtitle")}
            </Typography.Text>
          </Space>
        </Card>

        <ManualBillingCard fleetId={fleetId} billing={overview?.manualBilling} access={overview?.operationsAccess} onRefresh={loadOverview} />
        <Card
          style={{ display: overview?.manualBilling?.enabled ? 'none' : undefined, marginBottom: 16 }}
          title={t("billing.trial.title")}
          loading={loading}
          extra={
            trialStartAvailable ? (
              <Button type="primary" onClick={handleStartTrial} loading={startingTrial}>
                {t("billing.trial.start")}
              </Button>
            ) : null
          }
        >
          {trialActive ? (
            <Row gutter={[12, 12]}>
              <Col xs={12} md={8}>
                <Statistic title={t("billing.trial.remainingDays")} value={trial.remainingDays || 0} />
              </Col>
              <Col xs={12} md={8}>
                <Statistic
                  title={t("billing.trial.remainingEdocs")}
                  value={trial.remainingEdocs || 0}
                  suffix={`/ ${trial.edocLimit || 240}`}
                />
              </Col>
              <Col xs={12} md={8}>
                <Statistic title={t("billing.trial.status")} value={t("billing.trial.active")} />
              </Col>
              <Col xs={24}>
                <Alert type="success" showIcon message={t("billing.trial.activeMessage")} />
              </Col>
            </Row>
          ) : trialStartAvailable ? (
            <Alert
              type="info"
              showIcon
              message={t("billing.trial.availableTitle")}
              description={t("billing.trial.availableDescription")}
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message={t(`billing.trial.statuses.${trial.status || "unavailable"}`, {
                defaultValue: t("billing.trial.unavailableTitle"),
              })}
              description={t("billing.trial.unavailableDescription")}
            />
          )}
        </Card>

        <Row gutter={[16, 16]} style={{ display: overview?.manualBilling?.enabled ? 'none' : undefined }}>
          <Col xs={24} xl={15}>
            <Card
              title={
                <Space>
                  <RiseOutlined />
                  <span>{t("billing.usage.title")}</span>
                </Space>
              }
              loading={loading}
            >
              <Row gutter={[12, 12]}>
                <Col xs={12} md={8}>
                  <Statistic
                    title={t("billing.usage.currentDrivers")}
                    value={overview?.usage?.drivers?.currentRegisteredDrivers || 0}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title={t("billing.usage.billedDrivers")}
                    value={overview?.usage?.drivers?.billedQuantity || 0}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title={t("billing.usage.driverOverage")}
                    value={overview?.usage?.drivers?.overageCount || 0}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title={t("billing.usage.currentCommitment")}
                    value={overview?.usage?.etd?.commitmentQuantity || 0}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title={t("billing.usage.issuedThisPeriod")}
                    value={overview?.usage?.etd?.issuedCount || 0}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title={t("billing.usage.forecast")}
                    value={overview?.usage?.etd?.forecastIssuedCount || 0}
                  />
                </Col>
              </Row>

              <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 16 }}>
                {(overview?.usage?.warnings || []).map((warning) => (
                  <Alert
                    key={`${warning.family}-${warning.code}`}
                    type={warning.severity === "error" ? "error" : "warning"}
                    showIcon
                    message={t(`billing.warnings.${warning.code}.title`)}
                    description={t(`billing.warnings.${warning.code}.description`, warning)}
                  />
                ))}
                {(overview?.usage?.warnings || []).length === 0 ? (
                  <Alert
                    type="success"
                    showIcon
                    message={t("billing.usage.healthyTitle")}
                    description={t("billing.usage.healthyDescription")}
                  />
                ) : null}
              </Space>
            </Card>
          </Col>

          <Col xs={24} xl={9}>
            <Card
              title={
                <Space>
                  <FundProjectionScreenOutlined />
                  <span>{t("billing.summary.title")}</span>
                </Space>
              }
              loading={loading}
            >
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label={t("billing.summary.subscriptionStatus")}>
                  <Tag color={subscriptionStatusColor(currentSubscriptionStatus)}>
                    {t(`billing.status.${currentSubscriptionStatus}`, {
                      defaultValue: currentSubscriptionStatus || t("billing.status.inactive"),
                    })}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("billing.summary.dandfPlan")}>
                  {currentDandfPlan
                    ? t(`billing.catalog.${currentDandfPlan.slug}.name`, { defaultValue: currentDandfPlan.name })
                    : t("billing.none")}
                </Descriptions.Item>
                <Descriptions.Item label={t("billing.summary.etdPlan")}>
                  {currentEtdPlan
                    ? t(`billing.catalog.${currentEtdPlan.slug}.name`, { defaultValue: currentEtdPlan.name })
                    : t("billing.none")}
                </Descriptions.Item>
                <Descriptions.Item label={t("billing.summary.period")}>
                  {`${formatDate(overview?.currentSubscription?.currentPeriodStart)} — ${formatDate(
                    overview?.currentSubscription?.currentPeriodEnd
                  )}`}
                </Descriptions.Item>
              </Descriptions>

              <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 16 }}>
                <Tag color={overview?.entitlements?.driverPremiumSponsored ? "green" : "default"}>
                  {overview?.entitlements?.driverPremiumSponsored
                    ? t("billing.entitlements.driverPremiumSponsored")
                    : t("billing.entitlements.driverPremiumDisabled")}
                </Tag>
                <Tag color={overview?.entitlements?.etdEnabled ? "green" : "default"}>
                  {overview?.entitlements?.etdEnabled
                    ? t("billing.entitlements.etdEnabled")
                    : t("billing.entitlements.etdDisabled")}
                </Tag>
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 4, display: overview?.manualBilling?.enabled ? 'none' : undefined }}>
          <Col xs={24}>
            <Card title={t("billing.catalog.dandfSectionTitle")} loading={loading}>
              <Space direction="vertical" style={{ width: "100%" }} size={12}>
                <Typography.Text type="secondary">{t("billing.catalog.dandfSectionHelp")}</Typography.Text>
                <div className="billing-plan-grid billing-plan-grid-dandf">
                  {dandfPlans.length ? dandfPlans.map((plan) => {
                    const isSelected = selectedDandfPlanKey === plan.key;
                    const isCurrent = currentDandfPlanKey === plan.key;
                    const features = buildDandfPlanFeatures(plan, t);
                    return (
                      <button
                        key={plan.key}
                        type="button"
                        className={`billing-plan-card${isSelected ? " is-selected" : ""}${isCurrent ? " is-current" : ""}`}
                        onClick={() => setSelectedDandfPlanKey(plan.key)}
                      >
                        <div className="billing-plan-card-top">
                          <div className="billing-plan-card-badges">
                            {isCurrent ? <Tag color="green">{t("billing.catalog.currentPlan")}</Tag> : null}
                            {!isCurrent && isSelected ? <Tag color="blue">{t("billing.catalog.selectedPlan")}</Tag> : null}
                          </div>
                          <Typography.Title level={4} style={{ margin: 0 }}>
                            {t(`billing.catalog.${plan.slug}.name`, { defaultValue: plan.name })}
                          </Typography.Title>
                        </div>
                        <div className="billing-plan-price-box">
                          <Typography.Title level={2} style={{ margin: 0 }}>
                            {formatCurrency(plan.unitAmountNet, plan.currency || "EUR")}
                          </Typography.Title>
                          <Typography.Text type="secondary" className="billing-plan-price-caption">
                            {t("billing.pricing.perDriverShort", {
                              defaultValue: "per driver / month",
                            })}
                          </Typography.Text>
                        </div>
                        <div className="billing-plan-cta">
                          {isCurrent
                            ? t("billing.catalog.currentPlan")
                            : isSelected
                              ? t("billing.catalog.selectedPlan")
                              : t("billing.catalog.choosePlan")}
                        </div>
                        <ul className="billing-plan-features">
                          {features.map((feature) => (
                            <li key={`${plan.key}-${feature}`}>
                              <CheckCircleOutlined />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  }) : <Empty description={t("billing.catalog.noPlans", { defaultValue: "No paid plans available" })} />}
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24}>
            <Card title={t("billing.catalog.etdSectionTitle")} loading={loading}>
              <Space direction="vertical" style={{ width: "100%" }} size={12}>
                <Typography.Text type="secondary">{t("billing.catalog.etdSectionHelp")}</Typography.Text>
                <div className="billing-plan-grid billing-plan-grid-etd">
                  {etdPlans.length ? etdPlans.map((plan) => {
                    const isSelected = selectedEtdPlanKey === plan.key;
                    const isCurrent = currentEtdPlanKey === plan.key;
                    const features = buildEtdPlanFeatures(plan, t);
                    return (
                      <button
                        key={plan.key}
                        type="button"
                        className={`billing-plan-card${isSelected ? " is-selected" : ""}${isCurrent ? " is-current" : ""}`}
                        onClick={() => {
                          setSelectedEtdPlanKey(plan.key);
                        }}
                      >
                        <div className="billing-plan-card-top">
                          <div className="billing-plan-card-badges">
                            {isCurrent ? <Tag color="green">{t("billing.catalog.currentPlan")}</Tag> : null}
                            {!isCurrent && isSelected ? <Tag color="blue">{t("billing.catalog.selectedPlan")}</Tag> : null}
                          </div>
                          <Typography.Title level={4} style={{ margin: 0 }}>
                            {t(`billing.catalog.${plan.slug}.name`, { defaultValue: plan.name })}
                          </Typography.Title>
                        </div>
                        <div className="billing-plan-price-box">
                          <Typography.Title level={2} style={{ margin: 0 }}>
                            {formatCurrency(plan.unitAmountNet, plan.currency || "EUR")}
                          </Typography.Title>
                          <Typography.Text type="secondary" className="billing-plan-price-caption">
                            {t("billing.pricing.perEDocShort", {
                              defaultValue: "per issued eDoc",
                            })}
                          </Typography.Text>
                        </div>
                        <div className="billing-plan-cta">
                          {isCurrent
                            ? t("billing.catalog.currentPlan")
                            : isSelected
                              ? t("billing.catalog.selectedPlan")
                              : t("billing.catalog.choosePlan")}
                        </div>
                        <ul className="billing-plan-features">
                          {features.map((feature) => (
                            <li key={`${plan.key}-${feature}`}>
                              <CheckCircleOutlined />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  }) : <Empty description={t("billing.catalog.noPlans", { defaultValue: "No paid plans available" })} />}
                </div>

              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 4 }}>
          <Col xs={24} xl={10} style={{ display: overview?.manualBilling?.enabled ? 'none' : undefined }}>
            <Card title={t("billing.actions.title")} loading={loading}>
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Typography.Text type="secondary">{t("billing.actions.help")}</Typography.Text>
                <Space wrap>
                  <Button
                    type="primary"
                    onClick={handleSaveSelection}
                    loading={savingSelection}
                    disabled={!selectionDirty}
                  >
                    {hasActiveSubscription ? t("billing.actions.applySelection") : t("billing.actions.saveSelection")}
                  </Button>
                  <Button
                    onClick={handleCheckout}
                    loading={launchingCheckout}
                    disabled={!payableSelection || hasActiveSubscription}
                  >
                    {t("billing.actions.checkout")}
                  </Button>
                  <Button
                    onClick={handleOpenPortal}
                    loading={openingPortal}
                    disabled={!overview?.providerStatus?.portalAvailable}
                  >
                    {t("billing.actions.portal")}
                  </Button>
                </Space>
                {!overview?.providerStatus?.stripeConfigured ? (
                  <Alert type="info" showIcon message={t("billing.actions.stripeUnavailable")} />
                ) : null}
                {!overview?.providerStatus?.holdedConfigured ? (
                  <Alert type="info" showIcon message={t("billing.actions.holdedUnavailable")} />
                ) : null}
              </Space>
            </Card>
          </Col>

          <Col xs={24} xl={14}>
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  <span>{documentSectionTitle}</span>
                </Space>
              }
              loading={loading}
              style={{ marginTop: 16 }}
            >
              <List
                dataSource={overview?.invoices || []}
                locale={{ emptyText: <Empty description={documentEmptyText} /> }}
                renderItem={(invoice) => {
                  const isHoldedInvoice = String(invoice.source || "").trim().toLowerCase() === "holded";
                  const hostedActionLabel = isHoldedInvoice
                    ? t("billing.invoices.openHosted")
                    : t("billing.invoices.openHostedReceipt");

                  return (
                  <List.Item
                    actions={[
                      invoice.pdfUrl ? (
                        <Button
                          key={`${invoice.id}-pdf`}
                          size="small"
                          onClick={() => window.open(invoice.pdfUrl, "_blank", "noopener,noreferrer")}
                        >
                          {t("billing.invoices.openPdf")}
                        </Button>
                      ) : null,
                      invoice.hostedUrl ? (
                        <Button
                          key={`${invoice.id}-hosted`}
                          size="small"
                          onClick={() => window.open(invoice.hostedUrl, "_blank", "noopener,noreferrer")}
                        >
                          {hostedActionLabel}
                        </Button>
                      ) : null,
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>{invoice.number || invoice.id}</span>
                          <Tag color={invoice.holdedSyncStatus === "synced" ? "green" : "default"}>
                            {t(`billing.invoiceSources.${invoice.source}`, { defaultValue: invoice.source })}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={2}>
                          <Typography.Text type="secondary">
                            {t("billing.invoices.meta", {
                              status: t(`billing.status.${String(invoice.status || "").toLowerCase()}`, {
                                defaultValue: invoice.status || t("billing.status.inactive"),
                              }),
                              date: formatDate(invoice.issuedAt),
                            })}
                          </Typography.Text>
                          <Typography.Text strong>
                            {formatCurrency(invoice.totalAmount, invoice.currency || "EUR")}
                          </Typography.Text>
                        </Space>
                      }
                    />
                  </List.Item>
                  );
                }}
              />
            </Card>
          </Col>
        </Row>

        <Card
          title={
            <Space>
              <CreditCardOutlined />
              <span>{t("billing.profile.title")}</span>
            </Space>
          }
          loading={loading}
          style={{ marginTop: 16 }}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <Typography.Text strong>{t("billing.profile.legalName")}</Typography.Text>
              <Input value={legalName} onChange={(event) => setLegalName(event.target.value)} style={{ marginTop: 8 }} />
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text strong>{t("billing.profile.contactName")}</Typography.Text>
              <Input
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                style={{ marginTop: 8 }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text strong>{t("billing.profile.email")}</Typography.Text>
              <Input
                value={billingEmail}
                onChange={(event) => setBillingEmail(event.target.value)}
                style={{ marginTop: 8 }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text strong>{t("billing.profile.phone")}</Typography.Text>
              <Input
                value={billingPhone}
                onChange={(event) => setBillingPhone(event.target.value)}
                style={{ marginTop: 8 }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text strong>{t("billing.profile.vatNumber")}</Typography.Text>
              <Input value={vatNumber} onChange={(event) => setVatNumber(event.target.value)} style={{ marginTop: 8 }} />
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text strong>{t("billing.profile.billingAddress")}</Typography.Text>
              <Input
                value={billingAddress}
                onChange={(event) => setBillingAddress(event.target.value)}
                style={{ marginTop: 8 }}
              />
            </Col>
          </Row>

          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleSaveProfile} loading={savingProfile} disabled={!profileDirty}>
              {t("billing.profile.save")}
            </Button>
            <Typography.Text type="secondary">{t("billing.profile.help")}</Typography.Text>
          </Space>
        </Card>
      </div>
    </div>
  );
}
